import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    q: 'Is my patient data stored anywhere?',
    a: "Aura-Chart audio and transcripts are automatically deleted within 24 hours of your session. You can optionally save the generated note to your own device — notes are never stored on PulseStack's servers beyond that 24-hour window. Aura-Triage retains processed documents on encrypted, PHIPA-compliant Canadian servers so your team can access them, with strict role-based access controls.",
  },
  {
    q: 'Does this work with my EMR?',
    a: 'Yes — Aura-Chart works with any web-based EMR. It runs as a Chrome extension that sits on top of whatever you\'re already using (Accuro, OSCAR, TELUS CHR, Jane App, and others). Generate the note, paste it in. No IT ticket, no integration, no setup.',
  },
  {
    q: 'Does Aura-Chart adhere to PHIPA standards?',
    a: 'Yes. Aura-Chart is built to adhere to PHIPA standards — all data is processed and stored in Google Cloud Platform regions in Canada (Montreal and Toronto). We never transfer patient data to US-based servers. Full details are available at pulsestack.ai/security.',
  },
  {
    q: 'How accurate are the generated notes?',
    a: 'Aura-Chart uses Gemini 2.5 Pro with specialty-specific prompting, and Deepgram Nova-3 for transcription — chosen for their accuracy with medical terminology. Notes are generated to be reviewed and edited, not blindly accepted. You maintain full clinical responsibility for every note.',
  },
  {
    q: 'How long does it take to generate a note?',
    a: 'The note begins streaming within seconds of stopping the recording. A typical 10-minute consultation produces a complete SOAP note within 20–30 seconds, appearing word-by-word so you can start reading immediately.',
  },
  {
    q: 'What languages does it support?',
    a: 'Aura-Chart transcribes in English, French (Canadian), Spanish, Hindi, Urdu, Punjabi, Arabic, and auto-detects the language automatically — always outputting the note in English. A lot of Ontario clinics see patients who don\'t speak English as a first language, so we built this in from the start.',
  },
  {
    q: 'Do I need to install anything?',
    a: 'Aura-Chart is available as a Chrome extension (install in 60 seconds from the Chrome Web Store) and as a mobile app. No IT deployment, no server configuration, no EMR access required.',
  },
  {
    q: 'What happens after the 14-day trial?',
    a: 'At the end of your trial, you can choose a plan or your access will pause — no charges unless you subscribe. We\'ll send you a reminder before the trial ends so there are no surprises.',
  },
];

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="text-base font-medium text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 h-5 w-5 text-primary-600"
        >
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm text-gray-500 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">FAQ</p>
          <h2 className="mt-3 section-heading">Common questions</h2>
        </motion.div>

        <motion.div
          className="mt-10 divide-y divide-gray-100 rounded-2xl border border-gray-100 bg-gray-50 px-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </motion.div>

        <motion.p
          className="mt-8 text-center text-sm text-gray-400"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Still have questions?{' '}
          <a href="mailto:contact@pulsestack.ai" className="text-primary-600 hover:underline font-medium">
            Contact us
          </a>
        </motion.p>
      </div>
    </section>
  );
}
